import type { AdminSportsbook } from './api'
import { useEffect, useState } from 'react'
import { assetUrl } from '../api'
import { LOGO_ACCEPT, LOGO_MAX_BYTES, updateSportsbookBrand, uploadSportsbookLogo } from './api'

/**
 * BRAND IDENTITY panel for one sportsbook — color, logo (pasted path/URL or
 * uploaded file), fill flag. Shared by the Distribution sportsbook editor
 * (trading+) and the /affiliate operator page (affiliate+) so both roles edit
 * the same fields through the same RPC (update_sportsbook_brand, migration
 * 0066). Uploads land in the public `brand-logos` bucket; the CDN URL becomes
 * logo_url. Self-contained view/edit mode — the surrounding editor's own
 * EDIT/SAVE covers name + footprint only.
 */

const HEX_RE = /^#[0-9a-f]{6}$/i

type Mode = 'view' | 'edit'

interface Props {
  book: AdminSportsbook
  canEdit: boolean
  // Re-fetch the catalogs after a save so headers / sidebars / tiles pick up
  // the new values.
  onSaved: () => void
}

export default function SportsbookBrandPanel({ book, canEdit, onSaved }: Props) {
  const [mode, setMode] = useState<Mode>('view')
  const [brandColor, setBrandColor] = useState(book.brand_color ?? '')
  const [logoUrl, setLogoUrl] = useState(book.logo_url ?? '')
  const [logoFill, setLogoFill] = useState(book.logo_fill)
  const [logoFile, setLogoFile] = useState<File | null>(null)
  const [logoPreview, setLogoPreview] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [msg, setMsg] = useState<{ kind: 'ok' | 'err', text: string } | null>(null)

  useEffect(() => {
    setBrandColor(book.brand_color ?? '')
    setLogoUrl(book.logo_url ?? '')
    setLogoFill(book.logo_fill)
    setLogoFile(null)
    setMode('view')
    setMsg(null)
  }, [book.id, book.brand_color, book.logo_url, book.logo_fill])

  // Object URL for the picked file's live preview; revoked on change/unmount.
  useEffect(() => {
    if (!logoFile) {
      setLogoPreview(null)
      return
    }
    const url = URL.createObjectURL(logoFile)
    setLogoPreview(url)
    return () => URL.revokeObjectURL(url)
  }, [logoFile])

  function pickFile(f: File | null) {
    setMsg(null)
    if (f && f.size > LOGO_MAX_BYTES) {
      setMsg({ kind: 'err', text: `"${f.name}" is ${(f.size / 1024 / 1024).toFixed(2)} MB — the limit is 2 MB` })
      setLogoFile(null)
      return
    }
    setLogoFile(f)
  }

  function cancel() {
    setBrandColor(book.brand_color ?? '')
    setLogoUrl(book.logo_url ?? '')
    setLogoFill(book.logo_fill)
    setLogoFile(null)
    setMode('view')
    setMsg(null)
  }

  async function save() {
    const color = brandColor.trim()
    if (color && !HEX_RE.test(color)) {
      setMsg({ kind: 'err', text: `BRAND COLOR must be a #rrggbb hex (got "${color}")` })
      return
    }
    setBusy(true)
    setMsg(null)
    const hadFile = !!logoFile
    try {
      // Upload first so its CDN URL is what the brand write persists.
      let url: string | null = logoUrl.trim() || null
      if (logoFile) {
        const uploaded = await uploadSportsbookLogo(book.id, logoFile, logoFill)
        url = uploaded.logo_url
      }
      await updateSportsbookBrand(book.id, {
        brand_color: color || null,
        logo_url: url,
        logo_fill: logoFill,
      })
      setLogoFile(null)
      setLogoUrl(url ?? '')
      setMode('view')
      setMsg({ kind: 'ok', text: hadFile ? 'brand saved — logo uploaded' : 'brand saved' })
      onSaved()
    }
    catch (e) {
      setMsg({ kind: 'err', text: (e as Error).message })
    }
    finally {
      setBusy(false)
    }
  }

  const readOnly = mode === 'view'
  const previewColor = HEX_RE.test(brandColor.trim()) ? brandColor.trim() : null
  const previewSrc = logoPreview ?? assetUrl(logoUrl.trim() || null)
  const initials = book.name.slice(0, 2).toUpperCase()

  return (
    <div className="ad-editor-section sbp">
      <style>{PANEL_CSS}</style>
      <div className="sbp-head">
        <h3 className="b-mono">BRAND IDENTITY</h3>
        {canEdit && (
          readOnly
            ? (
                <button type="button" className="b-btn b-btn-ghost sbp-btn" onClick={() => { setMode('edit'); setMsg(null) }}>
                  EDIT →
                </button>
              )
            : (
                <span className="sbp-actions">
                  <button type="button" className="b-btn b-btn-primary sbp-btn" disabled={busy} onClick={save}>
                    {busy ? (logoFile ? 'UPLOADING…' : 'SAVING…') : 'SAVE'}
                  </button>
                  <button type="button" className="b-btn b-btn-ghost sbp-btn" disabled={busy} onClick={cancel}>
                    CANCEL
                  </button>
                </span>
              )
        )}
      </div>
      <p className="b-mono-dim ad-section-hint">
        What every tile renders — distribution grids, availability maps, the
        bet slip. Upload a logo (png / jpeg / webp / svg / gif, ≤ 2 MB) or
        paste a path; uploads are stored in Supabase Storage and served from
        its CDN.
      </p>
      {msg && (
        <p className={`b-mono sbp-msg${msg.kind === 'err' ? ' is-err' : ''}`}>
          {msg.kind === 'err' ? '⚠ ' : '✓ '}
          {msg.text}
        </p>
      )}

      <div className="sbp-rows">
        <div className="sbp-row">
          <span className="b-mono sbp-label">BRAND COLOR</span>
          {readOnly
            ? (
                book.brand_color
                  ? (
                      <span className="sbp-value">
                        <span className="sbp-swatch" style={{ background: book.brand_color }} aria-hidden="true" />
                        <span className="b-mono">{book.brand_color}</span>
                      </span>
                    )
                  : <span className="b-mono-dim">not set</span>
              )
            : (
                <span className="ad-color-row sbp-color-row">
                  <input
                    type="color"
                    className="ad-color-pick"
                    value={previewColor ?? '#0db5ff'}
                    onChange={e => setBrandColor(e.target.value)}
                    aria-label="Brand color picker"
                  />
                  <input
                    className="ad-input sbp-input"
                    value={brandColor}
                    onChange={e => setBrandColor(e.target.value)}
                    placeholder="#rrggbb — blank = none"
                    spellCheck={false}
                  />
                </span>
              )}
        </div>

        <div className="sbp-row">
          <span className="b-mono sbp-label">LOGO</span>
          {readOnly
            ? (
                book.logo_url
                  ? (
                      <span className="sbp-value">
                        <span
                          className={`ad-tile-preview${book.logo_fill ? ' is-fill' : ''}`}
                          style={{ background: book.brand_color ?? 'transparent' }}
                          aria-hidden="true"
                        >
                          <img src={assetUrl(book.logo_url)} alt="" loading="lazy" />
                        </span>
                        <span className="b-mono sbp-path">{book.logo_url}</span>
                      </span>
                    )
                  : <span className="b-mono-dim">not set</span>
              )
            : (
                <span className="sbp-logo-edit">
                  <span className="ad-file-row">
                    <span
                      className={`ad-tile-preview${logoFill ? ' is-fill' : ''}`}
                      style={{ background: previewColor ?? 'transparent' }}
                      aria-hidden="true"
                    >
                      {previewSrc
                        ? <img src={previewSrc} alt="" />
                        : <span className="ad-tile-preview-text">{initials}</span>}
                    </span>
                    <label className="b-btn b-btn-ghost sbp-btn" style={{ cursor: busy ? 'default' : 'pointer' }}>
                      {logoFile ? 'CHANGE FILE…' : 'UPLOAD FILE…'}
                      <input
                        type="file"
                        className="ad-file-input"
                        accept={LOGO_ACCEPT}
                        disabled={busy}
                        onChange={e => pickFile(e.target.files?.[0] ?? null)}
                      />
                    </label>
                    {logoFile && (
                      <>
                        <span className="ad-file-name">{logoFile.name}</span>
                        <button type="button" className="b-btn-ghost b-tiny" disabled={busy} onClick={() => pickFile(null)}>REMOVE</button>
                      </>
                    )}
                  </span>
                  <input
                    className="ad-input sbp-input sbp-url"
                    value={logoUrl}
                    onChange={e => setLogoUrl(e.target.value)}
                    placeholder="/sportsbooks/<file>.png or https://… — replaced by the upload if a file is chosen"
                    spellCheck={false}
                    disabled={!!logoFile}
                  />
                </span>
              )}
        </div>

        <div className="sbp-row">
          <span className="b-mono sbp-label">LOGO FILL</span>
          {readOnly
            ? <span className="b-mono">{book.logo_fill ? 'yes — logo covers tile edge-to-edge' : 'no — brand tile + padded logo'}</span>
            : (
                <label className="sbp-toggle">
                  <input
                    type="checkbox"
                    className="ad-checkbox"
                    checked={logoFill}
                    onChange={e => setLogoFill(e.target.checked)}
                  />
                  <span className="b-mono">logo covers the tile edge-to-edge (brand color baked into the image)</span>
                </label>
              )}
        </div>
      </div>
    </div>
  )
}

const PANEL_CSS = `
.sbp-head { display: flex; align-items: center; gap: 16px; }
.sbp-head h3 { margin: 0; }
.sbp-actions { display: inline-flex; gap: 8px; }
.sbp-btn { font-size: 11px; padding: 3px 10px; cursor: pointer; }
.sbp-msg { font-size: 12px; margin: 6px 0 0; }
.sbp-msg.is-err { color: #b91c1c; }
.brutalist-root.is-dark .sbp-msg.is-err { color: #f87171; }
.sbp-rows { display: flex; flex-direction: column; gap: 8px; margin-top: 8px; }
.sbp-row {
  display: grid;
  grid-template-columns: minmax(120px, 160px) 1fr;
  gap: 12px;
  align-items: center;
  font-size: 12px;
}
.sbp-label { opacity: 0.7; }
.sbp-value { display: inline-flex; align-items: center; gap: 8px; min-width: 0; }
.sbp-path { word-break: break-all; }
.sbp-swatch {
  width: 18px;
  height: 18px;
  border-radius: 4px;
  border: 1px solid rgba(128, 128, 128, 0.4);
  flex: 0 0 18px;
}
.sbp-color-row { max-width: 320px; }
.sbp-input { font-size: 12px; }
.sbp-logo-edit { display: flex; flex-direction: column; gap: 8px; min-width: 0; }
.sbp-url { width: 100%; max-width: 640px; }
.sbp-toggle { display: inline-flex; align-items: center; gap: 8px; font-size: 12px; cursor: pointer; }
@media (max-width: 700px) {
  .sbp-row { grid-template-columns: 1fr; gap: 4px; }
}
`
